/**
 * Clone Program Modal
 * 
 * Modal for cloning drug programs to multiple pharmacies with customization options
 */

import { useState, useEffect } from 'react'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator
} from './ui'

export default function CloneProgramModal({ program, isOpen, onClose, onClone, availablePharmacies = [] }) {
  const [selectedPharmacies, setSelectedPharmacies] = useState([])
  const [cloneOptions, setCloneOptions] = useState({
    preservePricing: false,
    updateDrugNames: true,
    copyFillSchedule: true,
    copyTags: true,
    reason: ''
  })
  const [pharmacyOverrides, setPharmacyOverrides] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPharmacies([])
      setPharmacyOverrides({})
      setCloneOptions({
        preservePricing: false,
        updateDrugNames: true,
        copyFillSchedule: true,
        copyTags: true,
        reason: ''
      })
    }
  }, [isOpen])

  const handlePharmacyToggle = (pharmacy) => {
    setSelectedPharmacies(prev => {
      const isSelected = prev.find(p => p.id === pharmacy.id)
      if (isSelected) {
        // Remove pharmacy and its overrides
        const newOverrides = { ...pharmacyOverrides }
        delete newOverrides[pharmacy.id]
        setPharmacyOverrides(newOverrides)
        return prev.filter(p => p.id !== pharmacy.id)
      } else {
        // Add pharmacy
        return [...prev, pharmacy]
      }
    })
  }

  const updatePharmacyOverride = (pharmacyId, field, value) => {
    setPharmacyOverrides(prev => ({
      ...prev,
      [pharmacyId]: {
        ...prev[pharmacyId],
        [field]: value
      }
    }))
  }

  const calculateEstimatedPrice = (pharmacyId) => {
    const override = pharmacyOverrides[pharmacyId]
    if (override?.customPrice) return override.customPrice
    
    const originalPrice = program?.pharmacy_specific?.current_price || 0
    if (cloneOptions.preservePricing) return originalPrice
    
    // Apply some variance for different pharmacies (demo logic)
    const variance = (pharmacyId.charCodeAt(0) % 10 - 5) * 0.02 // ±10%
    return Math.round(originalPrice * (1 + variance))
  }

  const formatPrice = (price) => {
    if (!price) return 'N/A'
    return `$${(price / 100).toFixed(2)}`
  }

  const handleClone = async () => {
    if (selectedPharmacies.length === 0) {
      alert('Please select at least one pharmacy to clone to.')
      return
    }

    setIsLoading(true)
    try {
      const cloneRequest = {
        sourceProgram: {
          id: program._id,
          name: program.drug_name
        },
        targetPharmacies: selectedPharmacies.map(pharmacy => ({
          pharmacyId: pharmacy.id || pharmacy._id,
          pharmacyName: pharmacy.pharmacy_name,
          overrides: {
            customPrice: pharmacyOverrides[pharmacy.id]?.customPrice,
            customDrugName: pharmacyOverrides[pharmacy.id]?.customDrugName,
            ...pharmacyOverrides[pharmacy.id]
          }
        })),
        options: cloneOptions
      }
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      console.log('Clone request:', cloneRequest)
      
      onClone?.(cloneRequest)
      onClose()
    } catch (error) {
      console.error('Clone failed:', error)
      alert('Failed to clone program: ' + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  if (!program) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Clone Program - {program.drug_name}</DialogTitle>
          <DialogDescription>
            Clone this drug program to multiple pharmacies with customization options.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Source Program Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Source Program</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Program:</span>
                  <div className="font-medium">{program.drug_name}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Entity ID:</span>
                  <div className="font-medium">{program.entity_id}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Current Price:</span>
                  <div className="font-medium">{formatPrice(program.pharmacy_specific?.current_price)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Type:</span>
                  <div className="font-medium capitalize">{program.dosespot_type}</div>
                </div>
              </div>
              
              {program.tags && program.tags.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm text-muted-foreground">Tags: </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {program.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clone Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Clone Options</CardTitle>
              <CardDescription>
                Configure how the program should be copied to target pharmacies.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="preservePricing"
                    checked={cloneOptions.preservePricing}
                    onCheckedChange={(checked) => setCloneOptions(prev => ({ ...prev, preservePricing: checked }))}
                  />
                  <Label htmlFor="preservePricing">Preserve Original Pricing</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="updateDrugNames"
                    checked={cloneOptions.updateDrugNames}
                    onCheckedChange={(checked) => setCloneOptions(prev => ({ ...prev, updateDrugNames: checked }))}
                  />
                  <Label htmlFor="updateDrugNames">Adapt Drug Names to Pharmacy</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="copyFillSchedule"
                    checked={cloneOptions.copyFillSchedule}
                    onCheckedChange={(checked) => setCloneOptions(prev => ({ ...prev, copyFillSchedule: checked }))}
                  />
                  <Label htmlFor="copyFillSchedule">Copy Fill Schedule</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="copyTags"
                    checked={cloneOptions.copyTags}
                    onCheckedChange={(checked) => setCloneOptions(prev => ({ ...prev, copyTags: checked }))}
                  />
                  <Label htmlFor="copyTags">Copy Tags</Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Cloning (Optional)</Label>
                <Input
                  id="reason"
                  value={cloneOptions.reason}
                  onChange={(e) => setCloneOptions(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Expanding program availability, new pharmacy onboarding..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Pharmacy Selection */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Target Pharmacies</CardTitle>
                  <CardDescription>
                    Select pharmacies to clone this program to. ({selectedPharmacies.length} selected)
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPharmacies([...availablePharmacies])}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPharmacies([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto">
                {availablePharmacies.map((pharmacy) => {
                  const isSelected = selectedPharmacies.find(p => p.id === pharmacy.id)
                  const estimatedPrice = calculateEstimatedPrice(pharmacy.id)
                  
                  return (
                    <div
                      key={pharmacy.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => handlePharmacyToggle(pharmacy)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{pharmacy.pharmacy_name}</div>
                          <div className="text-sm text-muted-foreground">
                            NCPDP: {pharmacy.pharmacy_ncpdpid} • {pharmacy.states?.length || 0} states
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Gateway: {pharmacy.pharmacy_gateway}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">
                            Est: {formatPrice(estimatedPrice)}
                          </div>
                          {isSelected && (
                            <Badge variant="default" className="text-xs">
                              Selected
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Pharmacy-specific overrides */}
                      {isSelected && (
                        <div className="mt-3 pt-3 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Custom Price (cents)</Label>
                              <Input
                                size="sm"
                                type="number"
                                placeholder={estimatedPrice.toString()}
                                value={pharmacyOverrides[pharmacy.id]?.customPrice || ''}
                                onChange={(e) => updatePharmacyOverride(
                                  pharmacy.id, 
                                  'customPrice', 
                                  parseInt(e.target.value) || null
                                )}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Custom Drug Name</Label>
                              <Input
                                size="sm"
                                placeholder={program.drug_name}
                                value={pharmacyOverrides[pharmacy.id]?.customDrugName || ''}
                                onChange={(e) => updatePharmacyOverride(
                                  pharmacy.id, 
                                  'customDrugName', 
                                  e.target.value
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {availablePharmacies.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No pharmacies available for cloning.</p>
                  <p className="text-sm">The program may already be available at all pharmacies.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clone Summary */}
          {selectedPharmacies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Clone Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p>
                    <span className="font-medium">{program.drug_name}</span> will be cloned to{' '}
                    <span className="font-medium">{selectedPharmacies.length}</span> pharmacies:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPharmacies.map((pharmacy) => (
                      <Badge key={pharmacy.id} variant="outline">
                        {pharmacy.pharmacy_name}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1 mt-3">
                    <p>• {cloneOptions.preservePricing ? 'Original pricing will be preserved' : 'Pricing will be adjusted per pharmacy'}</p>
                    <p>• {cloneOptions.updateDrugNames ? 'Drug names will be adapted to each pharmacy' : 'Original drug name will be used'}</p>
                    <p>• {cloneOptions.copyFillSchedule ? 'Fill schedules will be copied' : 'Fill schedules will be omitted'}</p>
                    <p>• {cloneOptions.copyTags ? 'Tags will be copied' : 'Tags will be omitted'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleClone} 
            disabled={isLoading || selectedPharmacies.length === 0}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner w-4 h-4 mr-2"></div>
                Cloning to {selectedPharmacies.length} pharmacies...
              </>
            ) : (
              `Clone to ${selectedPharmacies.length} Pharmacies`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
